import { indexAfterRemoval, isVideoSource } from '../../shared/utils/media';
import {
    Box,
    Button,
    Typography,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    IconButton,
    Checkbox,
    FormControlLabel,
    Alert,
    Divider,
    Paper,
    Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSwipeable } from 'react-swipeable';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/axios';
import { getWheelSourceLabel } from '../../shared/utils/wheelSource';
import BrandModelSelector from '../../widgets/components/BrandModelSelector';
import Autocomplete from '@mui/material/Autocomplete';

const radiusOptions = [17, 18, 19, 20, 21, 22, 23];
const typeOptions = ['Кованный', 'Литой'];

type MediaPreview = {
    file?: File;
    originalPath?: string;
    type: 'image' | 'video';
    src: string;
};

type WheelShort = {
    _id: string;
    name: string;
    diameter?: string;
    width?: string;
    pcd?: string;
    et?: string;
    dia?: string;
    type?: string;
};

type WheelSetItem = {
    wheelId: string;
    role: 'FRONT' | 'REAR' | null;
    qty: number;
};

type WheelSet = {
    _id: string;
    kind: 'UNIFORM' | 'STAGGERED';
    name?: string;
    items: WheelSetItem[];
    createdAt?: string;
};

const WheelDetailPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [source, setSource] = useState<string>('');

    const [form, setForm] = useState({
        diameter: '',
        et: '',
        dia: '',
        pcd: '',
        name: '',
        type: '',
        modelIds: [] as string[],
        price: '',
        isMy: false,
        width: '',
        bestseller: false,
    });

    const [errors, setErrors] = useState<{ name?: string }>({});
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [files, setFiles] = useState<File[]>([]);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [deletedMedia, setDeletedMedia] = useState<string[]>([]);
    const [previews, setPreviews] = useState<MediaPreview[]>([]);

    // комплекты
    const [wheelSets, setWheelSets] = useState<WheelSet[]>([]);
    const [setsLoading, setSetsLoading] = useState(false);
    const [setsError, setSetsError] = useState<string>('');

    const [setKind, setSetKind] = useState<'UNIFORM' | 'STAGGERED'>('UNIFORM');

    const [frontWheel, setFrontWheel] = useState<WheelShort | null>(null);
    const [rearWheel, setRearWheel] = useState<WheelShort | null>(null);

    const [frontSearch, setFrontSearch] = useState('');
    const [rearSearch, setRearSearch] = useState('');
    const [wheelOptions, setWheelOptions] = useState<WheelShort[]>([]);
    const [wheelOptionsLoading, setWheelOptionsLoading] = useState(false);

    const isEdit = Boolean(id);

    const loadWheelSets = async () => {
        if (!id) return;
        try {
            setSetsError('');
            setSetsLoading(true);
            const res = await api.get(`/api/admin/wheels/${id}/sets`);
            setWheelSets(res.data.data || []);
        } catch (e) {
            console.error('Ошибка загрузки комплектов:', e);
            setSetsError('Не удалось загрузить комплекты для диска.');
        } finally {
            setSetsLoading(false);
        }
    };

    const searchWheels = async (q: string) => {
        try {
            setWheelOptionsLoading(true);

            const res = await api.get('/api/admin/wheels', {
                params: { page: 1, limit: 20, search: q || '' },
            });

            const data: WheelShort[] = (res.data.data || []).map((w: any) => ({
                _id: w._id,
                name: w.name,
                diameter: w.diameter,
                width: w.width,
                pcd: w.pcd,
                et: w.et,
                dia: w.dia,
                type: w.type,
            }));

            setWheelOptions(data);
        } catch (e) {
            console.error('Ошибка поиска дисков:', e);
        } finally {
            setWheelOptionsLoading(false);
        }
    };

    const renderWheelOptionLabel = (w: WheelShort | string) => {
        if (typeof w === 'string') return w;

        const baseName = w?.name?.trim() || '(без названия)';

        const parts = [
            baseName,
            w.diameter ? `R${w.diameter}` : '',
            w.width ? `W${w.width}` : '',
            w.pcd ? w.pcd : '',
            w.et ? `ET${w.et}` : '',
            w.dia ? `DIA${w.dia}` : '',
        ].filter(Boolean);

        return parts.join(' • ');
    };


    const handleCreateSet = async () => {
        if (!id) return;

        try {
            setSetsError('');

            if (setKind === 'UNIFORM') {
                await api.post('/api/admin/wheel-sets', {
                    kind: 'UNIFORM',
                    name: `Одноширокий комплект: ${form.name}`,
                    items: [{ wheelId: id, role: null, qty: 4 }],
                });

                await loadWheelSets();
                return;
            }

            if (!frontWheel || !rearWheel) {
                setSetsError('Для разноширокого комплекта выберите FRONT и REAR диски.');
                return;
            }
            if (frontWheel._id === rearWheel._id) {
                setSetsError('FRONT и REAR не могут быть одним и тем же диском.');
                return;
            }

            await api.post('/api/admin/wheel-sets', {
                kind: 'STAGGERED',
                name: `Разноширокий комплект: ${form.name}`,
                items: [
                    { wheelId: frontWheel._id, role: 'FRONT', qty: 2 },
                    { wheelId: rearWheel._id, role: 'REAR', qty: 2 },
                ],
            });

            setFrontWheel(null);
            setRearWheel(null);
            await loadWheelSets();
        } catch (e) {
            console.error('Ошибка создания комплекта:', e);
            setSetsError('Не удалось создать комплект.');
        }
    };

    const handleDeleteSet = async (setId: string) => {
        if (!window.confirm('Удалить этот комплект?')) return;

        try {
            await api.delete(`/api/admin/wheel-sets/${setId}`);
            await loadWheelSets();
        } catch (e) {
            console.error('Ошибка удаления комплекта:', e);
            setSetsError('Не удалось удалить комплект.');
        }
    };

    useEffect(() => {
        if (!isEdit) return;

        const fetchWheel = async () => {
            try {
                const res = await api.get(`/api/admin/wheels/${id}`);
                const wheel = res.data;
                setSource(wheel.source || '');

                setForm({
                    diameter: wheel.diameter || '',
                    et: wheel.et || '',
                    dia: wheel.dia || '',
                    pcd: wheel.pcd || '',
                    name: wheel.name || '',
                    type: wheel.type || '',
                    modelIds: wheel.modelIds || [],
                    price: String(wheel.price || ''),
                    isMy: wheel.isMy || false,
                    width: wheel.width || '',
                    bestseller: wheel.bestseller || false,
                });

                const prod = process.env.REACT_APP_PROD ? true : false;
                const API_URL = prod ? '' : 'http://localhost:8833';

                if (wheel.media && Array.isArray(wheel.media)) {
                    const mediaPreviews: MediaPreview[] = wheel.media.map((p: string) => {
                        const full = p.startsWith('http') ? p : `${API_URL}/${p}`;
                        const isVideo = isVideoSource(full);

                        return { type: isVideo ? 'video' : 'image', src: full, originalPath: p };
                    });

                    setPreviews(mediaPreviews);

                    if (wheel.preview) {
                        const previewUrl = wheel.preview.startsWith('http')
                            ? wheel.preview
                            : `${API_URL}/${wheel.preview}`;

                        const foundIndex = mediaPreviews.findIndex((mp) => mp.src === previewUrl);

                        if (foundIndex !== -1) {
                            setPreviewIndex(foundIndex);
                            setSelectedIndex(foundIndex);
                        } else {
                            setPreviewIndex(0);
                            setSelectedIndex(0);
                        }
                    } else {
                        setPreviewIndex(0);
                        setSelectedIndex(0);
                    }
                }
            } catch (err) {
                console.error('Ошибка загрузки диска:', err);
            }
        };

        fetchWheel();
    }, [id, isEdit]);

    useEffect(() => {
        if (!isEdit) return;
        loadWheelSets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!isEdit) return;
        searchWheels('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!isEdit) return;

        const t = setTimeout(() => {
            searchWheels(frontSearch);
        }, 350);

        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [frontSearch]);

    useEffect(() => {
        if (!isEdit) return;

        const t = setTimeout(() => {
            searchWheels(rearSearch);
        }, 350);

        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rearSearch]);

    const handleChange = (field: string, value: string | boolean) => {
        if (field === 'pcd' && typeof value === 'string') {
            const digits = value.replace(/[^\d]/g, '');
            if (digits.length >= 2) {
                const pcdValue = `${digits[0]}x${digits.slice(1, 4)}`;
                setForm((prev) => ({ ...prev, pcd: pcdValue }));
                return;
            }
        }
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const newFiles = Array.from(e.target.files);

        const newPreviews: MediaPreview[] = newFiles.map((file) => {
            const type = file.type.startsWith('video') ? 'video' : 'image';
            return { type, src: URL.createObjectURL(file), file };
        });

        setFiles((prev) => [...prev, ...newFiles]);
        setPreviews((prev) => [...prev, ...newPreviews]);
    };

    const handleRemoveFile = (index: number) => {
        const removed = previews[index];
        if (removed.originalPath) setDeletedMedia(prev => [...prev, removed.originalPath!]);
        setFiles(prev => prev.filter(file => file !== removed.file));
        setPreviews(prev => prev.filter((_, i) => i !== index));
        setPreviewIndex(current => indexAfterRemoval(current, index, previews.length));
        setSelectedIndex(current => indexAfterRemoval(current, index, previews.length));
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (selectedIndex < previews.length - 1) setSelectedIndex(selectedIndex + 1);
        },
        onSwipedRight: () => {
            if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1);
        },
        trackMouse: true,
    });

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setErrors({ name: 'Наименование обязательно' });
            return;
        }

        const formData = new FormData();

        Object.entries(form).forEach(([key, value]) => {
            if (Array.isArray(value)) return;
            formData.append(key, String(value));
        });

        form.modelIds.forEach((mid) => formData.append('modelIds', mid));
        formData.append('previewIndex', String(previewIndex));
        files.forEach((file) => formData.append('media', file));

        deletedMedia.forEach((p) => formData.append('deletedMedia', p));

        if (isEdit) {
            previews.forEach(({ originalPath }) => {
                if (originalPath) formData.append('existingMedia', originalPath);
            });
        }

        try {
            if (isEdit) {
                await api.put(`/api/admin/wheels/${id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                await api.post('/api/admin/wheels', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }

            navigate('/wheels');
        } catch (err) {
            console.error('Ошибка при сохранении диска:', err);
        }
    };

    const currentWheelAsOption = (): WheelShort | null => {
        if (!id) return null;

        return {
            _id: id,
            name: form.name || '(текущий диск)',
            diameter: form.diameter,
            width: form.width,
            pcd: form.pcd,
            et: form.et,
            dia: form.dia,
            type: form.type,
        };
    };
    const wheelOptionsWithCurrent = (() => {
        const cur = currentWheelAsOption();
        if (!cur) return wheelOptions;

        return [cur, ...wheelOptions.filter(w => w._id !== cur._id)];
    })();

    return (
        <Box p={4}>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
                ← Назад
            </Button>

            <Typography variant="h5" gutterBottom>
                {isEdit ? 'Редактирование диска' : 'Создание нового диска'}
            </Typography>

            <Box component="form" sx={{ display: 'grid', gap: 2 }}>
                {isEdit && (
                    <TextField
                        label="Источник"
                        value={getWheelSourceLabel(source)}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                    />
                )}
                <FormControl fullWidth>
                    <InputLabel>Радиус</InputLabel>
                    <Select
                        value={String(form.diameter)}
                        label="Радиус"
                        onChange={(e) => handleChange('diameter', e.target.value)}
                    >
                        {radiusOptions.map((r) => (
                            <MenuItem key={r} value={r}>
                                {`R${r}`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Ширина"
                    value={form.width}
                    onChange={(e) => handleChange('width', e.target.value)}
                    fullWidth
                />
                <TextField
                    label="Вылет (ET)"
                    value={form.et}
                    onChange={(e) => handleChange('et', e.target.value)}
                    fullWidth
                />
                <TextField
                    label="DIA (центральное отверстие)"
                    value={form.dia}
                    onChange={(e) => handleChange('dia', e.target.value)}
                    fullWidth
                />
                <TextField
                    label="PCD (напр. 5x112)"
                    value={form.pcd}
                    onChange={(e) => handleChange('pcd', e.target.value)}
                    fullWidth
                />

                <TextField
                    label="Наименование"
                    value={form.name}
                    onChange={(e) => {
                        setErrors({});
                        handleChange('name', e.target.value);
                    }}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    fullWidth
                    required
                />

                <TextField
                    label="Цена"
                    type="number"
                    value={form.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    fullWidth
                />

                <FormControl fullWidth>
                    <InputLabel>Тип диска</InputLabel>
                    <Select
                        value={form.type}
                        label="Тип диска"
                        onChange={(e) => handleChange('type', e.target.value)}
                    >
                        {typeOptions.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox checked={form.isMy} onChange={(e) => handleChange('isMy', e.target.checked)} />
                    }
                    label="Мой диск"
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.bestseller}
                            onChange={(e) => handleChange('bestseller', e.target.checked)}
                        />
                    }
                    label="Хит продаж"
                />

                <BrandModelSelector
                    value={form.modelIds}
                    onChange={(modelIds) => setForm((prev) => ({ ...prev, modelIds }))}
                />

                <Button variant="contained" component="label">
                    Загрузить фото / видео
                    <input type="file" hidden multiple onChange={handleFileChange} />
                </Button>
                <Alert severity="warning" variant="filled">
                    Формат видео ТОЛЬКО mp4. Не устанавливайте видео как главное изображение.
                </Alert>
                {previews.length > 0 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Box sx={{ position: 'relative' }}>
                            <Box
                                sx={{
                                    width: 300,
                                    height: 200,
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                    position: 'relative',
                                }}
                            >
                                {previews[selectedIndex].type === 'video' ? (
                                    <video
                                        key={previews[selectedIndex].src}
                                        src={previews[selectedIndex].src}
                                        controls
                                        autoPlay
                                        muted
                                        playsInline
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            border: selectedIndex === previewIndex ? '3px solid #1976d2' : '1px solid #ccc',
                                            borderRadius: 8,
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={previews[selectedIndex].src}
                                        alt={`preview-${selectedIndex}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            border: selectedIndex === previewIndex ? '3px solid #1976d2' : '1px solid #ccc',
                                            borderRadius: 8,
                                        }}
                                    />
                                )}

                                <IconButton
                                    onClick={() => handleRemoveFile(selectedIndex)}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'white',
                                        color: 'red',
                                        '&:hover': { bgcolor: '#ffe5e5' },
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <Box
                                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 1 }}
                                {...swipeHandlers}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() => setSelectedIndex((prev) => Math.max(0, prev - 1))}
                                    disabled={selectedIndex === 0}
                                >
                                    ←
                                </Button>

                                <Typography variant="caption">
                                    {selectedIndex + 1} / {previews.length}
                                </Typography>

                                <Button
                                    variant="outlined"
                                    onClick={() => setSelectedIndex((prev) => Math.min(previews.length - 1, prev + 1))}
                                    disabled={selectedIndex === previews.length - 1}
                                >
                                    →
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}
                {previews.length > 0 && (
                    <Box display="flex" justifyContent="center" mt={2} gap={1}>
                        {previews.map((item, i) => (
                            <Box key={i} position="relative">
                                {item.type === 'video' ? (
                                    <video
                                        src={item.src}
                                        onClick={() => setSelectedIndex(i)}
                                        style={{
                                            width: 60,
                                            height: 60,
                                            objectFit: 'cover',
                                            border: i === selectedIndex ? '2px solid blue' : '1px solid #ccc',
                                            cursor: 'pointer',
                                        }}
                                        muted
                                        playsInline
                                    />
                                ) : (
                                    <img
                                        src={item.src}
                                        alt={`thumb-${i}`}
                                        onClick={() => setSelectedIndex(i)}
                                        style={{
                                            width: 60,
                                            height: 60,
                                            objectFit: 'cover',
                                            border: i === selectedIndex ? '2px solid blue' : '1px solid #ccc',
                                            cursor: 'pointer',
                                        }}
                                    />
                                )}
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveFile(i)}
                                    sx={{ position: 'absolute', top: -10, right: -10 }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
                {previews.length > 0 && (
                    <Box display="flex" justifyContent="center" gap={2} alignItems="center" mt={1}>
                        <Typography variant="caption">
                            {selectedIndex + 1} / {previews.length}
                        </Typography>

                        <Button
                            variant={selectedIndex === previewIndex ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setPreviewIndex(selectedIndex)}
                        >
                            {selectedIndex === previewIndex ? 'Главное изображение' : 'Сделать главным'}
                        </Button>
                    </Box>
                )}

                {isEdit && (
                    <>
                        <Divider sx={{ my: 2 }} />

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Комплекты (одноширокие / разноширокие)
                            </Typography>

                            {setsError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {setsError}
                                </Alert>
                            )}

                            <Box sx={{ display: 'grid', gap: 1, mb: 2 }}>
                                <Typography variant="subtitle2">Текущие комплекты</Typography>

                                {setsLoading ? (
                                    <Typography variant="body2">Загрузка...</Typography>
                                ) : wheelSets.length === 0 ? (
                                    <Typography variant="body2">Комплекты не созданы.</Typography>
                                ) : (
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                        {wheelSets.map((set) => (
                                            <Paper key={set._id} variant="outlined" sx={{ p: 1.5 }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                                                    <Box>
                                                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                                            <Chip size="small" label={set.kind === 'UNIFORM' ? 'Одноширокий' : 'Разноширокий'} />
                                                            <Typography variant="subtitle2">{set.name || 'Комплект'}</Typography>
                                                        </Box>

                                                        <Box mt={1} sx={{ display: 'grid', gap: 0.5 }}>
                                                            {set.items.map((it, idx) => (
                                                                <Box
                                                                    key={`${set._id}-${idx}`}
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gap={1}
                                                                    flexWrap="wrap"
                                                                >
                                                                    {it.role && <Chip size="small" label={it.role} />}
                                                                    <Chip size="small" variant="outlined" label={`qty: ${it.qty}`} />

                                                                    <Button size="small" variant="text" onClick={() => navigate(`/wheels/${it.wheelId}`)}>
                                                                        Открыть диск
                                                                    </Button>

                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {it.wheelId}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>

                                                    <Button color="error" variant="outlined" onClick={() => handleDeleteSet(set._id)}>
                                                        Удалить
                                                    </Button>
                                                </Box>
                                            </Paper>
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" gutterBottom>
                                Создать новый комплект
                            </Typography>

                            <FormControl fullWidth>
                                <InputLabel>Тип комплекта</InputLabel>
                                <Select value={setKind} label="Тип комплекта" onChange={(e) => setSetKind(e.target.value as any)}>
                                    <MenuItem value="UNIFORM">Одноширокий (4 одинаковых)</MenuItem>
                                    <MenuItem value="STAGGERED">Разноширокий (2 FRONT + 2 REAR)</MenuItem>
                                </Select>
                            </FormControl>

                            {setKind === 'UNIFORM' ? (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Будет создан комплект из 4 шт текущего диска.
                                </Alert>
                            ) : (
                                <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'start' }}>
                                        <Autocomplete
                                            options={wheelOptionsWithCurrent}
                                            value={frontWheel}
                                            onChange={(_, v) => {
                                                setFrontWheel(v);
                                                setFrontSearch(v ? renderWheelOptionLabel(v) : '');
                                            }}
                                            inputValue={frontSearch}
                                            onInputChange={(_, v, reason) => {
                                                if (reason === 'input') setFrontSearch(v);
                                            }}
                                            loading={wheelOptionsLoading}
                                            getOptionLabel={renderWheelOptionLabel}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option._id}>
                                                    {renderWheelOptionLabel(option)}
                                                </li>
                                            )}
                                            isOptionEqualToValue={(o, v) => o._id === v._id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="FRONT диск (поиск)"
                                                    helperText={wheelOptionsLoading ? 'Поиск...' : 'Введите часть названия'}
                                                    fullWidth
                                                />
                                            )}
                                            ListboxProps={{ style: { maxHeight: 320, overflow: 'auto' } }}
                                        />

                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                const cur = currentWheelAsOption();
                                                if (!cur) return;
                                                setFrontWheel(cur);
                                                setFrontSearch(renderWheelOptionLabel(cur));
                                            }}
                                            sx={{ mt: 0.25, whiteSpace: 'nowrap' }}
                                            disabled={!id}
                                        >
                                            Текущий диск
                                        </Button>
                                    </Box>



                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'start' }}>
                                        <Autocomplete
                                            options={wheelOptionsWithCurrent}
                                            value={rearWheel}
                                            onChange={(_, v) => {
                                                setRearWheel(v);
                                                setRearSearch(v ? renderWheelOptionLabel(v) : '');
                                            }}

                                            inputValue={rearSearch}
                                            onInputChange={(_, v, reason) => {
                                                if (reason === 'input') setRearSearch(v);
                                            }}
                                            loading={wheelOptionsLoading}
                                            getOptionLabel={renderWheelOptionLabel}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option._id}>
                                                    {renderWheelOptionLabel(option)}
                                                </li>
                                            )}
                                            isOptionEqualToValue={(o, v) => o._id === v._id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="REAR диск (поиск)"
                                                    helperText={wheelOptionsLoading ? 'Поиск...' : 'Введите часть названия'}
                                                    fullWidth
                                                />
                                            )}
                                            ListboxProps={{ style: { maxHeight: 320, overflow: 'auto' } }}
                                        />

                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                const cur = currentWheelAsOption();
                                                if (!cur) return;
                                                setRearWheel(cur);
                                                setRearSearch(renderWheelOptionLabel(cur));
                                            }}
                                            sx={{ mt: 0.25, whiteSpace: 'nowrap' }}
                                            disabled={!id}
                                        >
                                            Текущий диск
                                        </Button>
                                    </Box>

                                    <Alert severity="warning">Рекомендуется: FRONT и REAR должны отличаться (обычно шириной/ET).</Alert>
                                </Box>
                            )}

                            <Button sx={{ mt: 2 }} variant="contained" onClick={handleCreateSet}>
                                Создать комплект
                            </Button>
                        </Paper>
                    </>
                )}

                <Button variant="contained" color="primary" onClick={handleSubmit}>
                    {'Сохранить изменения'}
                </Button>
            </Box>
        </Box>
    );
};

export default WheelDetailPage;
